'use client';

import { useEffect, useState, useCallback } from 'react';
import createApp, { ClientApplication } from '@shopify/app-bridge';
import { useAppBridge } from '@shopify/app-bridge-react';
import { getSessionToken } from '@shopify/app-bridge-utils';
import { confirmSignIn, signIn } from 'aws-amplify/auth';
import { getRequest, handleSignOut, getCurrentUser } from '@/api/amplify/helpers';
// Stores
import { useUserDataStore } from '@/stores/userDataStore';

// Components
import { Card, EmptyState, Page } from '@shopify/polaris';
import AppLoading from '@/components/AppLoading';
import { IntegratedApp } from '@/constants/apps';
import { z } from 'zod';
import { AccountServiceClient } from '@/utils/apiClients';
import { useShallow } from 'zustand/react/shallow';
const CheckAppInstalledResponseSchema = z.object({
  isInstalled: z.boolean(),
});
type CheckAppInstalledResponse = z.infer<typeof CheckAppInstalledResponseSchema>;

const SignInEmbeddedAppV2ResponseSchema = z.object({
  owner: z.string(),
  appKey: z.string(),
  appUrl: z.string(),
  auth: z.string(),
});
type SignInEmbeddedAppV2Response = z.infer<typeof SignInEmbeddedAppV2ResponseSchema>;

const logIn = async ({ shopifyApp }: { shopifyApp: ClientApplication }) => {
  const shopifyUrl = shopify.config.shop;

  const sessionToken = await getSessionToken(shopifyApp!);

  const response = await AccountServiceClient.post<SignInEmbeddedAppV2Response>('/embedded/signin-v2', {
    appUrl: shopifyUrl,
    shopifyIdToken: sessionToken,
    appName: IntegratedApp.STUDIO_SHOPIFY,
  });
  console.log('response', response);
  const { owner, auth, appKey, appUrl } = response as SignInEmbeddedAppV2Response;
  try {
    await signIn({ username: owner, options: { authFlowType: 'CUSTOM_WITHOUT_SRP' } });
  } catch (error) {
    console.error('Error signing in', error);
    throw error;
  }
  try {
    const { isSignedIn } = await confirmSignIn({ challengeResponse: auth });

    if (!isSignedIn) {
      throw new Error('User is not signed in');
    }
  } catch (error) {
    console.error('Error confirming sign in', error);
    throw error;
  }

  return {
    appKey,
    appUrl,
    owner,
  };
};

const checkAppInstalled = async ({ appKey, appUrl }: { appKey: string; appUrl: string }): Promise<boolean> => {
  console.log('checking app installed', appKey, appUrl);
  const response: CheckAppInstalledResponse = await getRequest({
    apiName: 'accountService',
    path: '/account/check-app-installed',
    options: {
      queryParams: {
        appKey,
        appUrl,
        appName: IntegratedApp.STUDIO_SHOPIFY,
      },
    },
  });

  console.log('checkAppInstalled response', response);
  return response.isInstalled;
};

const reloginIfAppIsUninstalled = async ({
  appKey,
  appUrl,
  shopifyApp,
}: {
  appKey: string;
  appUrl: string;
  shopifyApp: ClientApplication;
}) => {
  const isAppInstalled = await checkAppInstalled({
    appKey,
    appUrl,
  });
  const userDataStore = useUserDataStore.getState();

  if (isAppInstalled) {
    userDataStore.setIsLoggedIn(true);
    userDataStore.setIsLoading(false);
    return;
  }
  console.log('App is uninstalled, relogin');
  await handleSignOut();
  const { appKey: newAppKey, appUrl: newAppUrl, owner } = await logIn({ shopifyApp });

  userDataStore.updateUserData({ appKey: newAppKey, appUrl: newAppUrl, owner });
};
type ShopifyIframeAuthProps = {
  children: React.ReactNode;
  serverPath?: string;
  host: string;
};

export const ShopifyIframeAuth = ({ children, host: initialHost }: ShopifyIframeAuthProps) => {
  const [error, setError] = useState<Error | null>(null);
  const shopify = useAppBridge();
  const {
    updateUserData,
    shopifyApp,
    appUrl: currentAppUrl,
    appKey: currentAppKey,
    setHost,
    isLoading,
    setIsLoggedIn,
    setIsLoading,
    isLoggedIn,
    setShopifyApp,
  } = useUserDataStore(
    useShallow((state) => ({
      updateUserData: state.updateUserData,
      shopifyApp: state.shopifyApp,
      appUrl: state.appUrl,
      appKey: state.appKey,
      setHost: state.setHost,
      isLoading: state.isLoading,
      setIsLoggedIn: state.setIsLoggedIn,
      setIsLoading: state.setIsLoading,
      isLoggedIn: state.isLoggedIn,
      setShopifyApp: state.setShopifyApp,
    }))
  );

  const initApp = useCallback(() => {
    if (initialHost) {
      setHost(initialHost);
      const app = createApp({
        apiKey: process.env.NEXT_SHOPIFY_CLIENT_ID!,
        host: initialHost,
        forceRedirect: true,
      });
      setShopifyApp(app);
    }
  }, [initialHost, setHost, setShopifyApp]);

  const initUserSession = useCallback(async () => {
    if (!shopify?.config.shop || !shopifyApp) {
      return;
    }

    try {
      const user = await getCurrentUser();
      const isUserLoggedIn = !!user;
      if (currentAppUrl === shopify.config.shop) {
        if (isUserLoggedIn) {
          setIsLoggedIn(true);
          // Intentionally not awaited, to not increase LCP
          reloginIfAppIsUninstalled({
            appKey: currentAppKey,
            appUrl: currentAppUrl,
            shopifyApp,
          });
          return;
        }
        const { appKey, appUrl, owner } = await logIn({ shopifyApp });
        updateUserData({ appKey, appUrl, owner });
        return;
      }
      if (isUserLoggedIn) {
        await handleSignOut();
      }
      const { appKey, appUrl, owner } = await logIn({ shopifyApp });
      updateUserData({ appKey, appUrl, owner });
    } catch (error) {
      console.error('Error logging in', error);
      setError(error as Error);
      setIsLoading(false);
    }
  }, [shopify, setIsLoggedIn, setIsLoading, updateUserData, currentAppUrl, currentAppKey, shopifyApp]);

  useEffect(() => {
    initApp();
  }, [initApp]);

  // TODO: In Shopify iframe this happens on every page navigation. Investigate an opportunity to optimize this.
  useEffect(() => {
    initUserSession();
  }, [initUserSession]);

  if (error) {
    return (
      <Page>
        <Card>
          <EmptyState
            heading="Error durring logging in"
            action={{
              content: 'Try again',
              onAction: () => window.location.reload(),
            }}
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>Please refresh the page or contact support.</p>
          </EmptyState>
        </Card>
      </Page>
    );
  }

  // if (isLoading) {
  //   return <AppLoading />;
  // }

  if (isLoggedIn || isLoading) {
    return children;
  }

  return (
    <Page>
      <Card>
        <EmptyState
          heading="Could not log in"
          action={{
            content: 'Refresh the page',
            onAction: () => window.location.reload(),
          }}
          image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
        >
          <p>Please refresh the page or contact support.</p>
        </EmptyState>
      </Card>
    </Page>
  );
};
